/**
 * Hook for managing FHIR ConceptMap operations
 * Handles loading and using ConceptMaps for exam-consent mappings
 */

import { useState, useEffect, useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { ConceptMap, ServiceRequest } from '@medplum/fhirtypes';
import {
  getRequiredConsentCodes,
  getRequiredConsentsForExam,
  isConsentRequiredForExam,
} from '@/utils/consent.utils';
import { ARKON } from '@/consts/systems.consts';

/**
 * Hook for managing ConceptMap state and operations
 * @returns Object containing ConceptMap state and service functions
 */
export function useConceptMap() {
  const medplum = useMedplum();

  // ConceptMap state
  const [conceptMap, setConceptMap] = useState<ConceptMap | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load ConceptMap from server
   */
  const loadConceptMap = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const map = await medplum.searchOne('ConceptMap', {
        identifier: `${ARKON.CM.BASE}|${ARKON.CM.VALUES.GENETIC_TEST_TO_CONSENT}`,
      });
      
      if (map) {
        setConceptMap(map);
      } else {
        setError('ConceptMap not found on server');
      }
    } catch (err) {
      console.error('Failed to load ConceptMap from server:', err);
      setError('Failed to load consent requirements');
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  // Load ConceptMap on mount
  useEffect(() => {
    loadConceptMap();
  }, [loadConceptMap]);

  /**
   * Check if a specific consent is required for a ServiceRequest
   * @param serviceRequest - The ServiceRequest to check
   * @param consentCode - The consent code to check
   * @returns True if the consent is required for this ServiceRequest
   */
  const isConsentRequired = (
    serviceRequest: ServiceRequest,
    consentCode: string
  ): boolean => {
    if (!conceptMap) {
      return false;
    }

    const examCode = serviceRequest.code?.coding?.[0]?.code;
    if (!examCode) {
      return false;
    }
    
    return isConsentRequiredForExam(examCode, consentCode, conceptMap);
  };

  /**
   * Get all required consent codes for a ServiceRequest
   * @param serviceRequest - The ServiceRequest to get required consent codes for
   * @returns Array of required consent codes
   */
  const getRequiredConsentCodesForServiceRequest = (
    serviceRequest: ServiceRequest
  ): string[] => {
    if (!conceptMap) {
      return ['general-consent', 'scientific-use']; // Default requirements
    }

    const examCode = serviceRequest.code?.coding?.[0]?.code;
    if (!examCode) {
      return ['general-consent', 'scientific-use']; // Default requirements
    }
    return getRequiredConsentCodes(examCode, conceptMap);
  };

  /**
   * Get exam type display name from ServiceRequest
   * @param serviceRequest - The ServiceRequest to get exam type display for
   * @returns Display name of the exam type
   */
  const getExamTypeDisplay = (serviceRequest: ServiceRequest): string => {
    const examCode = serviceRequest.code?.coding?.[0]?.code;
    const display = serviceRequest.code?.coding?.[0]?.display;

    if (display) {
      return display;
    }

    return examCode || 'Unknown Exam Type';
  };

  /**
   * Validate that all required consents are provided
   * @param serviceRequest - The ServiceRequest to validate consents for
   * @param consentData - The consent data to validate (contains general, scientificUse, photoVideoUse, incidentalFindings, secondaryFindings)
   * @param consentData.general - The general consent value
   * @param consentData.scientificUse - The scientific use consent value
   * @param consentData.photoVideoUse - The photo/video consent value
   * @param consentData.incidentalFindings - The incidental findings consent value
   * @param consentData.secondaryFindings - The secondary findings consent value
   * @returns Validation result with isValid flag and missing consent codes
   */
  const validateRequiredConsents = (
    serviceRequest: ServiceRequest,
    consentData: {
      general: boolean | null;
      scientificUse: boolean | null;
      photoVideoUse: boolean | null;
      incidentalFindings: boolean | null;
      secondaryFindings: boolean | null;
    }
  ): { isValid: boolean; missingConsents: string[] } => {
    if (!conceptMap) {
      return {
        isValid: false,
        missingConsents: ['concept-map-not-loaded'],
      };
    }

    const requiredConsents =
      getRequiredConsentCodesForServiceRequest(serviceRequest);
    const missingConsents: string[] = [];

    // Map consent codes to consent data properties
    const consentMapping = {
      'general-consent': consentData.general,
      'scientific-use': consentData.scientificUse,
      'photo-video': consentData.photoVideoUse,
      'incidental-findings': consentData.incidentalFindings,
      'secondary-findings': consentData.secondaryFindings,
    };

    // Check each required consent
    for (const consentCode of requiredConsents) {
      const consentValue =
        consentMapping[consentCode as keyof typeof consentMapping];
      
      if (consentValue === null) {
        missingConsents.push(consentCode);
      }
    }

    return {
      isValid: missingConsents.length === 0,
      missingConsents,
    };
  };

  /**
   * Get consent requirements summary for display
   * @param serviceRequest - The ServiceRequest to get consent requirements summary for
   * @returns Object containing required consents and exam type
   */
  const getConsentRequirementsSummary = (
    serviceRequest: ServiceRequest
  ): {
    required: string[];
    examType: string;
  } => {
    const examCode = serviceRequest.code?.coding?.[0]?.code;
    const examType = getExamTypeDisplay(serviceRequest);

    if (!examCode || !conceptMap) {
      return {
        required: ['general-consent', 'scientific-use'],
        examType,
      };
    }

    const requirements = getRequiredConsentsForExam(examCode, conceptMap);
    const required = requirements.map((req) => req.code);

    return {
      required,
      examType,
    };
  };

  return {
    // State
    conceptMap,
    loading,
    error,

    // Actions
    loadConceptMap,

    // Service functions
    isConsentRequired,
    getRequiredConsentCodesForServiceRequest,
    getExamTypeDisplay,
    validateRequiredConsents,
    getConsentRequirementsSummary,
  };
}
